import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ClipboardList,
  User,
  Car,
  Boxes,
  Trash2,
  Plus,
  ChevronDown,
  Wallet,
  StickyNote,
  Bell,
  Keyboard,
  FileText,
  UploadCloud,
  Eye,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import {
  createOrder, ordersByMobile, getOrder, updateOrder, submitDraft, listParts,
  uploadOrderAttachment, deleteOrderAttachment, downloadOrderAttachmentUrl,
} from "@/lib/api";
import { fmtDate, STATUS_LABELS, STATUS_COLORS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const newRow = () => ({ id: `tmp-${Date.now()}-${Math.random()}`, part_number: "", part_name: "", qty: 1, receive_status: "pending", availability: "to_order" });

// Indian mobile validation: 10 digits, optionally prefixed with +91 / 0 / 91
const normalizeMobile = (s) => (s || "").replace(/\D/g, "").slice(-10);
const isValidMobile = (s) => /^[6-9]\d{9}$/.test(normalizeMobile(s));

const CreatePartsRequest = () => {
  const { currentUser, currentLocation, locations } = useApp();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [editStatus, setEditStatus] = useState(null); // status of the order being edited (e.g. "draft")

  // Estimate / attachment state
  const [pendingEstimate, setPendingEstimate] = useState(null); // File object queued for upload after order create
  const [existingAttachments, setExistingAttachments] = useState([]); // [{id, filename, size, ...}]
  const [uploadingEstimate, setUploadingEstimate] = useState(false);
  const estimateInputRef = useRef(null);

  const [form, setForm] = useState({
    order_against: "job_card",
    job_card_no: "",
    customer_name: "",
    whatsapp_number: "",
    town: "",
    vin_no: "",
    registration_no: "",
    model_name: "",
    color: "",
    parts: [newRow()],
    priority: "normal",
    advance_date: "",
    advance_amount: "",
    advance_mode: "",
    advance_remarks: "",
    internal_notes: "",
    location_code: "",
  });

  // Customer history popup
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyOrders, setHistoryOrders] = useState([]);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Refs for keyboard navigation / focus on error
  const partNumberRefs = useRef([]);
  const customerNameRef = useRef(null);
  const mobileRef = useRef(null);
  const modelRef = useRef(null);

  useEffect(() => {
    if (currentLocation && currentLocation !== "ALL") {
      setForm((f) => ({ ...f, location_code: currentLocation }));
    } else if (currentUser?.location_codes?.length) {
      setForm((f) => ({ ...f, location_code: currentUser.location_codes[0] }));
    }
  }, [currentLocation, currentUser]);

  // Once real locations load, ensure form.location_code is one of the valid ones.
  // Fixes stale codes (e.g. user profile still has "BAM" but admin removed that location).
  useEffect(() => {
    if (!locations.length) return;
    const allowed =
      currentUser?.role === "admin"
        ? locations
        : locations.filter((l) => currentUser?.location_codes?.includes(l.code));
    if (!allowed.length) return;
    setForm((f) => {
      if (allowed.find((l) => l.code === f.location_code)) return f;
      return { ...f, location_code: allowed[0].code };
    });
  }, [locations, currentUser]);

  useEffect(() => {
    if (editId) {
      (async () => {
        const o = await getOrder(editId);
        setEditStatus(o.status);
        setExistingAttachments(o.attachments || []);
        setForm({
          order_against: o.order_against,
          job_card_no: o.job_card_no || "",
          customer_name: o.customer_name,
          whatsapp_number: o.whatsapp_number,
          town: o.town || "",
          vin_no: o.vin_no || "",
          registration_no: o.registration_no || "",
          model_name: o.model_name,
          color: o.color || "",
          parts: o.parts.length ? o.parts : [newRow()],
          priority: o.priority,
          advance_date: o.advance?.date || "",
          advance_amount: o.advance?.amount || "",
          advance_mode: o.advance?.mode || "",
          advance_remarks: o.advance?.remarks || "",
          internal_notes: o.internal_notes || "",
          location_code: o.location_code,
        });
      })();
    }
  }, [editId]);

  const setField = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: null }));
  };

  const setPart = (idx, key, val) => {
    setForm((f) => {
      const parts = [...f.parts];
      parts[idx] = { ...parts[idx], [key]: val };
      return { ...f, parts };
    });
    if (errors.parts?.[idx]?.[key]) {
      setErrors((e) => {
        const next = { ...e, parts: [...(e.parts || [])] };
        next.parts[idx] = { ...(next.parts[idx] || {}), [key]: null };
        return next;
      });
    }
  };

  const addRow = (focusIdx = null) => {
    setForm((f) => {
      const newParts = [...f.parts, newRow()];
      if (focusIdx === null) focusIdx = newParts.length - 1;
      // Focus the new row's part_number after render
      setTimeout(() => {
        const el = partNumberRefs.current[focusIdx];
        if (el) el.focus();
      }, 30);
      return { ...f, parts: newParts };
    });
  };

  const removeRow = (idx) =>
    setForm((f) => ({ ...f, parts: f.parts.filter((_, i) => i !== idx) }));

  // Row-selection state used for the "Mark Selected as In Stock" bulk action.
  const [selectedRows, setSelectedRows] = useState(new Set());
  const toggleSelected = (idx) => setSelectedRows((prev) => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    return next;
  });
  const clearSelection = () => setSelectedRows(new Set());
  const selectAll = () => setSelectedRows(new Set(form.parts.map((_, i) => i)));
  const bulkMarkInStock = () => {
    if (!selectedRows.size) return;
    setForm((f) => ({
      ...f,
      parts: f.parts.map((p, i) => selectedRows.has(i) ? { ...p, availability: "in_stock" } : p),
    }));
    clearSelection();
  };

  // Keyboard shortcut: Alt+N adds new line item
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && (e.key === "n" || e.key === "N")) {
        e.preventDefault();
        addRow();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, []);

  const onQtyKeyDown = (idx, e) => {
    const isLast = idx === form.parts.length - 1;
    if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey && isLast)) {
      e.preventDefault();
      if (isLast) {
        addRow();
      } else {
        // Move to next row's part_number
        const next = partNumberRefs.current[idx + 1];
        if (next) next.focus();
      }
    }
  };

  const onMobileBlur = async () => {
    if (!form.whatsapp_number || editId) return;
    try {
      const hist = await ordersByMobile(form.whatsapp_number);
      if (hist.length > 0) {
        setHistoryOrders(hist);
        setHistoryOpen(true);
      }
    } catch (err) {
      // Customer history is a soft feature — log but don't block submit.
      console.warn("Failed to fetch customer order history:", err);
    }
  };

  // Returns { fieldErrors, firstErrorRef }; empty object => OK
  const validateForSubmit = () => {
    const e = { parts: form.parts.map(() => ({})) };
    let firstFocus = null;
    if (!form.location_code) e.location_code = "Location required";
    if (form.order_against === "job_card" && !form.job_card_no) e.job_card_no = "Job Card No required";
    if (!form.customer_name) {
      e.customer_name = "Customer Name required";
      firstFocus = firstFocus || customerNameRef;
    }
    if (!form.whatsapp_number) {
      e.whatsapp_number = "Mobile number required";
      firstFocus = firstFocus || mobileRef;
    } else if (!isValidMobile(form.whatsapp_number)) {
      e.whatsapp_number = "Enter valid 10-digit mobile number";
      firstFocus = firstFocus || mobileRef;
    }
    if (form.order_against === "counter_sale" && !form.town) e.town = "Town required";
    if (!form.model_name) {
      e.model_name = "Vehicle Model required";
      firstFocus = firstFocus || modelRef;
    }
    if (form.parts.length === 0) {
      toast.error("Cannot submit order. Add at least one part.");
      return { ok: false, fieldErrors: e };
    }
    form.parts.forEach((p, i) => {
      if (!p.part_name) {
        e.parts[i].part_name = "Required";
        if (!firstFocus) firstFocus = { current: partNumberRefs.current[i] };
      }
      if (!p.part_number) {
        e.parts[i].part_number = "Required";
        if (!firstFocus) firstFocus = { current: partNumberRefs.current[i] };
      }
      if (!p.qty || Number(p.qty) <= 0) {
        e.parts[i].qty = "Required";
        if (!firstFocus) firstFocus = { current: partNumberRefs.current[i] };
      }
    });
    const hasFieldErr = Object.keys(e).some((k) => k !== "parts" && e[k]);
    const hasRowErr = e.parts.some((row) => Object.keys(row).length > 0);
    const ok = !hasFieldErr && !hasRowErr;
    return { ok, fieldErrors: e, firstFocus };
  };

  const submit = async (status) => {
    setErrors({});
    if (status !== "draft") {
      const { ok, fieldErrors, firstFocus } = validateForSubmit();
      if (!ok) {
        setErrors(fieldErrors);
        toast.error("Cannot submit order. Mandatory information missing.");
        setTimeout(() => firstFocus?.current?.focus(), 30);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        location_code: form.location_code,
        order_against: form.order_against,
        job_card_no: form.order_against === "job_card" ? form.job_card_no : null,
        customer_name: form.customer_name,
        whatsapp_number: form.whatsapp_number ? normalizeMobile(form.whatsapp_number) : "",
        town: form.order_against === "counter_sale" ? form.town : null,
        vin_no: form.vin_no || null,
        registration_no: form.registration_no || null,
        model_name: form.model_name,
        color: form.color || null,
        parts: form.parts.map((p) => ({
          id: p.id,
          part_number: p.part_number || "",
          part_name: p.part_name,
          qty: Number(p.qty) || 0,
          receive_status: p.receive_status || "pending",
          availability: p.availability === "in_stock" ? "in_stock" : "to_order",
        })),
        priority: form.priority,
        advance:
          form.advance_amount || form.advance_date || form.advance_mode
            ? {
                date: form.advance_date || null,
                amount: Number(form.advance_amount || 0),
                mode: form.advance_mode || null,
                remarks: form.advance_remarks || null,
              }
            : null,
        internal_notes: form.internal_notes || null,
        status,
        actor: currentUser.name,
        actor_role: currentUser.role,
      };
      let res;
      if (editId) {
        // Always save latest field edits first
        res = await updateOrder(editId, payload);
        // If editing a draft and the user clicked "Save & Submit Request",
        // we need to convert draft → new_order via the dedicated endpoint
        // (PUT /orders/{id} ignores `status` and never regenerates the IVA #).
        if (editStatus === "draft" && status !== "draft") {
          res = await submitDraft(editId, {
            actor: currentUser.name,
            actor_role: currentUser.role,
          });
          toast.success(`Draft submitted as ${res.order_number}`);
        } else {
          toast.success(`Order ${res.order_number} updated`);
        }
      } else {
        res = await createOrder(payload);
        toast.success(status === "draft"
          ? `Draft saved as ${res.order_number}`
          : `Order ${res.order_number} created`);
      }
      // Upload any queued estimate now that we have a saved order id
      if (pendingEstimate && res?.id) {
        try {
          await uploadOrderAttachment(
            res.id, pendingEstimate, currentUser.name, currentUser.role, "estimate"
          );
          toast.success(`Estimate "${pendingEstimate.name}" attached`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Estimate upload failed");
        }
      }
      navigate(status === "draft" ? "/orders" : "/workbench");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const userLocations =
    currentUser?.role === "admin"
      ? locations
      : locations.filter((l) => currentUser?.location_codes?.includes(l.code));

  // ===== Estimate upload helpers =====
  const MAX_ESTIMATE_MB = 10;
  const acceptEstimate = (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are allowed for estimates.");
      return;
    }
    if (file.size > MAX_ESTIMATE_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_ESTIMATE_MB} MB limit.`);
      return;
    }
    // If we already have a saved order (editing), upload immediately
    if (editId) {
      setUploadingEstimate(true);
      uploadOrderAttachment(editId, file, currentUser.name, currentUser.role, "estimate")
        .then((att) => {
          setExistingAttachments((arr) => [...arr, att]);
          toast.success(`Estimate "${file.name}" attached`);
        })
        .catch((e) => toast.error(e?.response?.data?.detail || "Upload failed"))
        .finally(() => {
          setUploadingEstimate(false);
          if (estimateInputRef.current) estimateInputRef.current.value = "";
        });
    } else {
      // New order — queue for upload after Save / Save & Submit
      setPendingEstimate(file);
    }
  };

  const removeExistingAttachment = async (att) => {
    if (!window.confirm(`Remove "${att.filename}" from this order?`)) return;
    try {
      await deleteOrderAttachment(editId, att.id, currentUser.name, currentUser.role);
      setExistingAttachments((arr) => arr.filter((a) => a.id !== att.id));
      toast.success("Estimate removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const fmtSize = (b) => {
    if (!b && b !== 0) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-3.5" data-testid="create-parts-request-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[24px] font-semibold text-yamaha-800 leading-none tracking-tight">
            {editId ? "Edit Parts Request" : "Create Parts Request"}
          </h1>
          <p className="text-[12.5px] text-[#747684] mt-1">
            Fill in details below to initiate inventory or job-linked request.
          </p>
        </div>
        <div className="text-right text-[11px] text-[#747684] leading-snug">
          <div className="font-semibold text-[#1a1b22]">{currentUser?.name}</div>
          <div>{userLocations.find((l) => l.code === form.location_code)?.name || "—"}</div>
          <div>{new Date().toLocaleString("en-GB")} IST</div>
        </div>
      </div>

      {/* Location */}
      {(currentUser?.role === "admin" || (currentUser?.location_codes?.length || 0) > 1) && (
        <Section icon={<Boxes className="w-4 h-4" />} title="Location">
          <select
            data-testid="location-select"
            value={form.location_code}
            onChange={(e) => setField("location_code", e.target.value)}
            className="w-full md:w-1/3 border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
          >
            {userLocations.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name} ({l.code})
              </option>
            ))}
          </select>
        </Section>
      )}

      {/* Order Against */}
      <Section icon={<ClipboardList className="w-4 h-4" />} title="Order Against">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RadioCard
            checked={form.order_against === "job_card"}
            onChange={() => setField("order_against", "job_card")}
            label="Job Card Request"
            testid="radio-job-card"
          />
          <RadioCard
            checked={form.order_against === "counter_sale"}
            onChange={() => setField("order_against", "counter_sale")}
            label="Direct Counter Sale"
            testid="radio-counter-sale"
          />
        </div>
      </Section>

      {/* Customer Details */}
      <Section icon={<User className="w-4 h-4" />} title="Customer Details">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {form.order_against === "job_card" && (
            <Field label="Job Card No *">
              <input
                data-testid="job-card-input"
                value={form.job_card_no}
                onChange={(e) => setField("job_card_no", e.target.value.toUpperCase())}
                placeholder="JC-2026-0001"
                className="input uppercase"
              />
            </Field>
          )}
          <Field label="Customer Name *" error={errors.customer_name}>
            <input
              ref={customerNameRef}
              data-testid="customer-name-input"
              value={form.customer_name}
              onChange={(e) => setField("customer_name", e.target.value)}
              placeholder="John Doe"
              className={`input ${errors.customer_name ? "!border-rose-500 !ring-rose-200" : ""}`}
            />
          </Field>
          <Field label="Mobile Number *" error={errors.whatsapp_number} hint="10 digits — e.g. 9876543210">
            <input
              ref={mobileRef}
              data-testid="whatsapp-input"
              inputMode="numeric"
              maxLength={14}
              value={form.whatsapp_number}
              onChange={(e) => {
                const v = e.target.value.replace(/[^\d+ ]/g, "");
                setField("whatsapp_number", v);
              }}
              onBlur={onMobileBlur}
              placeholder="9876543210"
              className={`input ${errors.whatsapp_number ? "!border-rose-500 !ring-rose-200" : ""}`}
            />
          </Field>
          {form.order_against === "counter_sale" && (
            <Field label="Town *">
              <input
                data-testid="town-input"
                value={form.town}
                onChange={(e) => setField("town", e.target.value)}
                placeholder="Bangalore"
                className="input"
              />
            </Field>
          )}
        </div>
      </Section>

      {/* Vehicle Details */}
      <Section icon={<Car className="w-4 h-4" />} title="Vehicle Details">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="VIN (Chassis Number)">
            <input
              data-testid="vin-input"
              value={form.vin_no}
              onChange={(e) => setField("vin_no", e.target.value.toUpperCase())}
              placeholder="1HD1YG23..."
              className="input uppercase"
            />
          </Field>
          <Field label="Registration No.">
            <input
              data-testid="registration-input"
              value={form.registration_no}
              onChange={(e) => setField("registration_no", e.target.value.toUpperCase())}
              placeholder="KA-01-AB-1234"
              className="input uppercase"
            />
          </Field>
          <Field label="Model Name *" error={errors.model_name}>
            <input
              ref={modelRef}
              data-testid="model-input"
              value={form.model_name}
              onChange={(e) => setField("model_name", e.target.value)}
              placeholder="Yamaha FZ-S FI"
              className={`input ${errors.model_name ? "!border-rose-500 !ring-rose-200" : ""}`}
            />
          </Field>
          <Field label="Color">
            <input
              data-testid="color-input"
              value={form.color}
              onChange={(e) => setField("color", e.target.value)}
              placeholder="Matte Black"
              className="input"
            />
          </Field>
        </div>
      </Section>

      {/* Upload Estimate (PDF only · max 10 MB) */}
      <Section icon={<FileText className="w-4 h-4" />} title="Upload Estimate">
        <div className="space-y-3" data-testid="upload-estimate-section">
          <p className="text-[11.5px] text-[#747684] leading-relaxed -mt-1">
            Attach a Pymidol estimate PDF for this order (optional). PDF only, max {MAX_ESTIMATE_MB} MB.
            {!editId && (
              <span className="ml-1 text-[#1a1b22]">
                File will be uploaded when you click <span className="font-semibold">Save Draft</span> or <span className="font-semibold">Save &amp; Submit</span>.
              </span>
            )}
          </p>

          {/* Drop zone */}
          <div
            data-testid="estimate-dropzone"
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const f = e.dataTransfer.files?.[0];
              if (f) acceptEstimate(f);
            }}
            onClick={() => estimateInputRef.current?.click()}
            className="border-2 border-dashed border-[#cfcdd8] rounded-lg p-5 text-center hover:border-yamaha-400 hover:bg-yamaha-50/30 cursor-pointer transition-colors"
          >
            <UploadCloud className="w-7 h-7 mx-auto text-[#747684]" />
            <div className="text-[13px] font-semibold text-[#1a1b22] mt-2">
              {uploadingEstimate ? "Uploading…" : "Drop estimate PDF here, or click to browse"}
            </div>
            <div className="text-[11px] text-[#747684] mt-0.5">PDF · max {MAX_ESTIMATE_MB} MB</div>
            <input
              ref={estimateInputRef}
              data-testid="estimate-file-input"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) acceptEstimate(f);
              }}
            />
          </div>

          {/* Pending (queued) estimate for new orders */}
          {pendingEstimate && !editId && (
            <div
              data-testid="estimate-pending"
              className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-amber-200 bg-amber-50"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <FileText className="w-4 h-4 text-amber-700 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-[#1a1b22] truncate">{pendingEstimate.name}</div>
                  <div className="text-[10.5px] text-[#747684]">
                    {fmtSize(pendingEstimate.size)} · Will be attached on save
                  </div>
                </div>
              </div>
              <button
                type="button"
                data-testid="estimate-pending-remove"
                onClick={() => setPendingEstimate(null)}
                className="p-1.5 rounded hover:bg-amber-100 text-amber-700"
                title="Remove"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Existing attachments on a saved order */}
          {existingAttachments.length > 0 && (
            <div className="space-y-1.5" data-testid="estimate-existing-list">
              {existingAttachments.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-[#e2e1eb] bg-white hover:bg-[#fafaff]"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <FileText className="w-4 h-4 text-yamaha-700 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold text-[#1a1b22] truncate">{att.filename}</div>
                      <div className="text-[10.5px] text-[#747684]">
                        {fmtSize(att.size)} · Uploaded {att.uploaded_at ? new Date(att.uploaded_at).toLocaleString("en-GB") : ""}
                        {att.uploaded_by ? ` · by ${att.uploaded_by}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <a
                      href={downloadOrderAttachmentUrl(editId, att.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold border border-[#e2e1eb] hover:bg-yamaha-50"
                      data-testid={`estimate-view-${att.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> View
                    </a>
                    <button
                      type="button"
                      onClick={() => removeExistingAttachment(att)}
                      className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                      title="Remove"
                      data-testid={`estimate-remove-${att.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Parts Required */}
      <Section icon={<Boxes className="w-4 h-4" />} title="Parts Required">
        {/* Bulk action bar — visible only when at least one row is selected */}
        {selectedRows.size > 0 && (
          <div className="mb-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5"
               data-testid="bulk-availability-bar">
            <span className="text-[11.5px] font-semibold text-emerald-800">
              {selectedRows.size} selected
            </span>
            <span className="text-[#cfcdd8]">·</span>
            <button data-testid="bulk-mark-in-stock" onClick={bulkMarkInStock}
              className="text-[11.5px] px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              Mark Selected as In Stock
            </button>
            <button data-testid="bulk-select-all" onClick={selectAll}
              className="text-[11px] text-emerald-700 hover:underline">Select All</button>
            <button data-testid="bulk-clear-selection" onClick={clearSelection}
              className="text-[11px] text-[#747684] hover:underline">Clear</button>
          </div>
        )}
        <div className="space-y-2" data-testid="parts-list">
          {form.parts.map((p, idx) => {
            const rowErr = errors.parts?.[idx] || {};
            const rowHasErr = Object.keys(rowErr).length > 0;
            const isSelected = selectedRows.has(idx);
            const isInStock = p.availability === "in_stock";
            return (
            <div
              key={p.id}
              className={`bg-[#f4f3fc] rounded-md px-2.5 py-2 grid grid-cols-12 gap-2 items-center border ${
                rowHasErr ? "border-rose-500" : isInStock ? "border-emerald-300" : "border-transparent"
              }`}
              data-testid={`part-row-${idx}`}
            >
              <div className="col-span-1 flex items-center justify-center">
                {idx === 0 && (
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[#747684] block mb-0.5 sr-only">Sel</label>
                )}
                <input type="checkbox" checked={isSelected} onChange={() => toggleSelected(idx)}
                  data-testid={`part-select-${idx}`}
                  className="w-4 h-4 accent-emerald-600" />
              </div>
              <div className="col-span-3">
                {idx === 0 && (
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[#747684] block mb-0.5">
                    Part Number * {rowErr.part_number && <span className="text-rose-600 normal-case font-normal">· Required</span>}
                  </label>
                )}
                {idx !== 0 && rowErr.part_number && (
                  <div className="text-[10px] text-rose-600 font-semibold mb-0.5">Required</div>
                )}
                <PartAutocomplete
                  field="part_number"
                  testid={`part-number-${idx}`}
                  value={p.part_number}
                  inputRef={(el) => (partNumberRefs.current[idx] = el)}
                  placeholder="YP-55420-X"
                  hasError={!!rowErr.part_number}
                  onChange={(v) => setPart(idx, "part_number", v.toUpperCase())}
                  onPick={(pick) => {
                    setPart(idx, "part_number", pick.part_number);
                    setPart(idx, "part_name", pick.part_name);
                  }}
                />
              </div>
              <div className="col-span-3">
                {idx === 0 && (
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[#747684] block mb-0.5">
                    Part Name * {rowErr.part_name && <span className="text-rose-600 normal-case font-normal">· Required</span>}
                  </label>
                )}
                {idx !== 0 && rowErr.part_name && (
                  <div className="text-[10px] text-rose-600 font-semibold mb-0.5">Required</div>
                )}
                <PartAutocomplete
                  field="part_name"
                  testid={`part-name-${idx}`}
                  value={p.part_name}
                  placeholder="Brake Pad Set (Front)"
                  hasError={!!rowErr.part_name}
                  onChange={(v) => setPart(idx, "part_name", v)}
                  onPick={(pick) => {
                    setPart(idx, "part_name", pick.part_name);
                  }}
                />
              </div>
              <div className="col-span-1">
                {idx === 0 && (
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[#747684] block mb-0.5">
                    Qty * {rowErr.qty && <span className="text-rose-600 normal-case font-normal">· !</span>}
                  </label>
                )}
                {idx !== 0 && rowErr.qty && (
                  <div className="text-[10px] text-rose-600 font-semibold mb-0.5">Req</div>
                )}
                <input
                  type="number"
                  min="1"
                  data-testid={`part-qty-${idx}`}
                  value={p.qty}
                  onChange={(e) => setPart(idx, "qty", e.target.value)}
                  onKeyDown={(e) => onQtyKeyDown(idx, e)}
                  className={`input !py-1.5 !text-[12.5px] bg-white ${rowErr.qty ? "!border-rose-500" : ""}`}
                />
              </div>
              <div className="col-span-3">
                {idx === 0 && (
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[#747684] block mb-0.5">
                    Availability
                  </label>
                )}
                <select
                  data-testid={`part-availability-${idx}`}
                  value={p.availability || "to_order"}
                  onChange={(e) => setPart(idx, "availability", e.target.value)}
                  className={`input !py-1.5 !text-[12.5px] font-semibold ${
                    isInStock ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-white text-[#1a1b22]"
                  }`}
                >
                  <option value="to_order">To Order</option>
                  <option value="in_stock">In Stock</option>
                </select>
              </div>
              <div className={`col-span-1 flex justify-end ${idx === 0 ? "self-end pb-0.5" : ""}`}>
                <button
                  data-testid={`delete-row-${idx}`}
                  onClick={() => removeRow(idx)}
                  disabled={form.parts.length === 1}
                  className="text-[#747684] hover:text-red-600 p-1.5 rounded disabled:opacity-30"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            );
          })}
          <div className="pt-1">
            <button
              data-testid="add-row-btn"
              onClick={() => addRow()}
              className="inline-flex items-center gap-1.5 h-9 px-3 border border-dashed border-[#c4c5d5] rounded-md text-[12.5px] font-medium text-[#444653] hover:border-yamaha-400 hover:text-yamaha-700 hover:bg-yamaha-50/40 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add Row
            </button>
            <div className="mt-1 text-[10.5px] text-[#a4a6b3] inline-flex items-center gap-1 ml-2">
              <Keyboard className="w-3 h-3" />
              Shortcut: <kbd className="px-1 py-px rounded border border-[#e2e1eb] bg-[#faf8ff] font-mono text-[9px]">Alt+N</kbd>
              <span>or</span>
              <kbd className="px-1 py-px rounded border border-[#e2e1eb] bg-[#faf8ff] font-mono text-[9px]">Enter</kbd>
              <span>from Qty</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Priority */}
      <Section icon={<Bell className="w-4 h-4" />} title="Order Priority">
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="priority-normal">
            <input
              type="radio"
              checked={form.priority === "normal"}
              onChange={() => setField("priority", "normal")}
              className="accent-yamaha-700"
            />
            <span className="text-[13px]">Normal</span>
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
          </label>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="priority-urgent">
            <input
              type="radio"
              checked={form.priority === "urgent"}
              onChange={() => setField("priority", "urgent")}
              className="accent-yamaha-700"
            />
            <span className="text-[13px]">Urgent (V.O.R)</span>
            <span className="w-2 h-2 rounded-full bg-red-500" />
          </label>
        </div>
      </Section>

      {/* Advance Details */}
      <CollapsibleSection icon={<Wallet className="w-4 h-4" />} title="Advance Details" defaultOpen>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Date">
            <input
              type="date"
              data-testid="advance-date"
              value={form.advance_date}
              onChange={(e) => setField("advance_date", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Advance Amount">
            <input
              type="number"
              data-testid="advance-amount"
              value={form.advance_amount}
              onChange={(e) => setField("advance_amount", e.target.value)}
              placeholder="500"
              className="input"
            />
          </Field>
          <Field label="Payment Mode">
            <select
              data-testid="advance-mode"
              value={form.advance_mode}
              onChange={(e) => setField("advance_mode", e.target.value)}
              className="input"
            >
              <option value="">—</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
            </select>
          </Field>
          <Field label="Remarks">
            <input
              data-testid="advance-remarks"
              value={form.advance_remarks}
              onChange={(e) => setField("advance_remarks", e.target.value)}
              className="input"
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* Internal Notes */}
      <CollapsibleSection icon={<StickyNote className="w-4 h-4" />} title="Internal Notes">
        <textarea
          data-testid="internal-notes"
          value={form.internal_notes}
          onChange={(e) => setField("internal_notes", e.target.value)}
          rows="3"
          placeholder="Visible only to staff. Not sent to customer."
          className="input"
        />
      </CollapsibleSection>

      {/* Actions */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-[#e2e1eb] -mx-6 px-6 py-2.5 flex items-center justify-end gap-2.5 z-30">
        <div className="flex-1 text-[11px] text-[#747684] flex items-center gap-2 min-w-0">
          <Keyboard className="w-3 h-3 shrink-0 text-[#a4a6b3]" />
          <kbd className="px-1 py-px rounded border border-[#e2e1eb] bg-[#faf8ff] font-mono text-[9.5px]">Alt+N</kbd>
          <span className="text-[#a4a6b3]">add row</span>
          <span className="text-[#c4c5d5]">·</span>
          <kbd className="px-1 py-px rounded border border-[#e2e1eb] bg-[#faf8ff] font-mono text-[9.5px]">Enter</kbd>
          <span className="text-[#a4a6b3]">from Qty</span>
          <span className="text-[#c4c5d5] hidden md:inline">·</span>
          <span className="text-[#a4a6b3] hidden md:inline truncate">Draft saves without validation. Submit requires Customer, Mobile, Model, Part #, Name, Qty.</span>
        </div>
        <Button
          data-testid="save-draft-btn"
          variant="outline"
          onClick={() => submit("draft")}
          disabled={submitting}
          className="h-9 px-4 text-[12.5px]"
        >
          Save Draft
        </Button>
        <Button
          data-testid="save-request-btn"
          className="bg-yamaha-700 hover:bg-yamaha-800 text-white h-9 px-4 text-[12.5px]"
          onClick={() => submit("new_order")}
          disabled={submitting}
        >
          {editId
            ? (editStatus === "draft" ? "Save & Submit Request" : "Update Request")
            : "Save & Submit Request"}
        </Button>
      </div>

      {/* Customer History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent data-testid="customer-history-dialog">
          <DialogHeader>
            <DialogTitle>Customer Order History</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-[#f4f3fc]">
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Parts</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e1eb]">
                {historyOrders.map((o) => (
                  <tr key={o.id}>
                    <td className="px-3 py-2">{fmtDate(o.created_at)}</td>
                    <td className="px-3 py-2">
                      {o.parts?.map((p) => p.part_name).join(", ").slice(0, 60)}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_COLORS[o.status]}`}>
                        {STATUS_LABELS[o.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)} data-testid="history-close-btn">
              Close & Create New Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Section = ({ icon, title, children }) => (
  <div className="bg-white rounded-xl border border-[#e2e1eb] p-4">
    <div className="flex items-center gap-2 mb-2.5">
      <div className="w-7 h-7 rounded-md bg-yamaha-50 text-yamaha-700 flex items-center justify-center">
        {icon}
      </div>
      <h2 className="text-[13.5px] font-semibold text-[#1a1b22]">{title}</h2>
    </div>
    {children}
  </div>
);

const CollapsibleSection = ({ icon, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-[#e2e1eb]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center justify-between"
        data-testid={`collapse-${title.toLowerCase().replace(/\s/g, "-")}`}
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-yamaha-50 text-yamaha-700 flex items-center justify-center">
            {icon}
          </div>
          <h2 className="text-[13.5px] font-semibold text-[#1a1b22]">{title}</h2>
        </div>
        <ChevronDown className={`w-4 h-4 text-[#747684] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

// === Part Autocomplete with type-ahead from Parts Master ===
const PartAutocomplete = ({ field, testid, value, onChange, onPick, placeholder, inputRef, hasError }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  const query = value || "";

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setItems([]); setOpen(false); return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await listParts(query, 10);
        // Sort: prefix matches first
        const lower = query.toLowerCase();
        data.sort((a, b) => {
          const af = (a[field] || "").toLowerCase().startsWith(lower) ? 0 : 1;
          const bf = (b[field] || "").toLowerCase().startsWith(lower) ? 0 : 1;
          return af - bf;
        });
        setItems(data.slice(0, 10));
        // Only auto-open if input still focused
        const focused = document.activeElement === (inputRef ? document.querySelector(`[data-testid="${testid}"]`) : null);
        // simpler: open whenever we have results
        setOpen(true);
        setHighlight(0);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [query, field]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const choose = (idx) => {
    const it = items[idx];
    if (!it) return;
    onPick(it);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") {
      // Only intercept Enter if there's a real highlight that differs from current value
      const it = items[highlight];
      if (it) {
        e.preventDefault();
        choose(highlight);
      }
    } else if (e.key === "Escape") { setOpen(false); }
  };

  // Highlight matching substring in suggestion
  const renderMatch = (text) => {
    if (!query || !text) return text;
    const i = text.toLowerCase().indexOf(query.toLowerCase());
    if (i < 0) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark className="bg-amber-100 text-amber-900 px-0">{text.slice(i, i + query.length)}</mark>
        {text.slice(i + query.length)}
      </>
    );
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        ref={inputRef}
        data-testid={testid}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (items.length > 0) setOpen(true); }}
        onKeyDown={onKeyDown}
        autoComplete="off"
        className={`input !py-1.5 !text-[12.5px] bg-white ${hasError ? "!border-rose-500" : ""}`}
      />
      {open && items.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-40 bg-white rounded-md border border-[#e2e1eb] shadow-lg max-h-72 overflow-y-auto"
          data-testid={`${testid}-dropdown`}
        >
          {items.map((it, i) => (
            <button
              type="button"
              key={`${it.part_number}-${i}`}
              data-testid={`${testid}-option-${i}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => choose(i)}
              className={`w-full text-left px-3 py-1.5 flex items-center gap-3 ${i === highlight ? "bg-yamaha-50" : "hover:bg-[#faf8ff]"}`}
            >
              <span className="text-[11.5px] font-mono text-yamaha-700 min-w-[110px] truncate">
                {field === "part_number" ? renderMatch(it.part_number) : it.part_number}
              </span>
              <span className="text-[12px] text-[#1a1b22] truncate flex-1">
                {field === "part_name" ? renderMatch(it.part_name) : it.part_name}
              </span>
            </button>
          ))}
        </div>
      )}
      {loading && open && items.length === 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-40 bg-white rounded-md border border-[#e2e1eb] shadow-lg px-3 py-2 text-[11px] text-[#747684]">
          Searching…
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children, error, hint }) => (
  <div>
    <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684]">{label}</label>
    <div className="mt-1">{children}</div>
    {error ? (
      <div className="text-[10px] text-rose-600 mt-0.5 font-semibold">{error}</div>
    ) : hint ? (
      <div className="text-[10px] text-[#747684] mt-0.5">{hint}</div>
    ) : null}
  </div>
);

const RadioCard = ({ checked, onChange, label, testid }) => (
  <label
    data-testid={testid}
    className={`border rounded-md px-3 py-2 cursor-pointer flex items-center gap-2.5 transition-all ${
      checked ? "border-yamaha-700 ring-1 ring-yamaha-700 bg-yamaha-50" : "border-[#e2e1eb] hover:bg-[#f4f3fc]"
    }`}
  >
    <input type="radio" checked={checked} onChange={onChange} className="accent-yamaha-700" />
    <span className="text-[13px] font-medium">{label}</span>
  </label>
);

export default CreatePartsRequest;
